import { NavigateFunction } from "react-router-dom";
import { IDatabaseRecord } from "../dto/IDatabaseRecord";
import { ISimpleRecord } from "../dto/ISimpleRecord";
import { ensureSuccess, handleError } from "./ErrorHandling";
import { GlobalStateDispatch } from "../state/GlobalStateProvider";

export const fetchRecord = async <T extends ISimpleRecord> (
    publisherId: string | undefined,
    id: string | undefined,
    entitySet: string,
    setRecord: React.Dispatch<React.SetStateAction<T>>,
    globalDispatch: GlobalStateDispatch,
    defaultRecord?: T
) => {
    try
    {
        if (id && id !== "new") {
            const response = await fetch(`/api/publishers/${publisherId}${ entitySet !== "publishers" ? `/${entitySet}/${id}` : ""}`, {
                headers: {
                    "Content-Type": "application/json"
                }
            });

            await ensureSuccess(response);

            const body = await response.json() as T;
            setRecord(body);
            return body;
        }
        else {
            const fallback = defaultRecord ?? {} as T;
            setRecord(fallback);
            return fallback;
        }
    }
    catch(e)
    {
        handleError(e as Error, globalDispatch);
    }
};

export const fetchPublicRecord = async <T extends ISimpleRecord> (
    id: string | undefined,
    entitySet: string,
    setRecord: React.Dispatch<React.SetStateAction<T>>,
    globalDispatch: GlobalStateDispatch,
    defaultRecord?: T
) => {
    try
    {
        if (id && id !== "new") {
            const response = await fetch(`/api/public/${entitySet}/${id}`, {
                headers: {
                    "Content-Type": "application/json"
                }
            });

            await ensureSuccess(response);

            const body = await response.json() as T;
            setRecord(body);
        }
        else {
            setRecord(defaultRecord ?? {} as T);
        }
    }
    catch(e)
    {
        handleError(e as Error, globalDispatch);
    }
};

export const submit = async <T extends ISimpleRecord> (
    publisherId: string | undefined,
    id: string | undefined,
    record: IDatabaseRecord,
    entitySet: string,
    setRecord: React.Dispatch<React.SetStateAction<T>>,
    setIsSaving: React.Dispatch<React.SetStateAction<boolean>>,
    navigate: NavigateFunction,
    globalDispatch: GlobalStateDispatch
): Promise<T | undefined> => {
    let body: T | undefined = undefined;

    try {
        setIsSaving(true);

        const response = await fetch(`/api${publisherId ? `/publishers/${publisherId}` : ( entitySet === "publishers" ? "/publishers" : "")}${entitySet !== "publishers" ? `/${entitySet}` : ""}`, {
            method: record?.guid ? "PUT" : "POST",
            headers: {
                "Content-Type": "application/json"
            },        
            body: JSON.stringify(record)
        });

        await ensureSuccess(response);

        body = await response.json() as T;

        if (id === "new" && publisherId)
        {
            navigate(`/workspace/publishers/${publisherId}/${entitySet}/${body.guid}`, { state: { record: body } });
        }
        else if (id === "new" && !publisherId)
        {
            navigate(`/workspace/publishers/${body.guid}`, { state: { record: body } });
        }
        else
        {
            setRecord(body);
        }
    }
    catch (e) {
        handleError(e as Error, globalDispatch);
    }
    finally {
        setIsSaving(false);
    }

    return body;
};